/**
 * Player bars and the Gas display. UI_UX section 7.
 *
 * The opponent bar carries the avatar, Integrity and the public counts (hand,
 * deck, Dust) with the Dust count opening a viewer, since Dust is a public
 * ordered zone the player is entitled to read. The player bar adds a row of
 * Gas pips per chain showing available over total.
 */

import { useState, type ReactNode } from 'react';
import { CHAINS, type Chain, type GameState } from '@/engine';
import { profile } from '@/engine';
import { CardFrame, viewFromObject } from '@/ui/cards';
import { FactionGlyph } from '@/ui/icons/FactionGlyph';
import { UiIcon } from '@/ui/icons/UiIcon';
import { Modal } from '@/ui/system';
import styles from './Battlefield.module.css';

export interface PlayerBarProps {
  state: GameState;
  playerId: string;
  /** Stable hook for the tutorial spotlight. */
  tutorialId?: string;
  /** Targeting mode wants this player as a possible target. */
  targetable?: boolean;
  /** Attackers are pointed at this player right now. */
  underAttack?: boolean;
  onAvatarClick?: () => void;
  /** Rendered at the end of the bar. Gas pips for the player, nothing else. */
  trailing?: ReactNode;
}

export function PlayerBar({
  state,
  playerId,
  tutorialId,
  targetable,
  underAttack,
  onAvatarClick,
  trailing,
}: PlayerBarProps) {
  const [dustOpen, setDustOpen] = useState(false);
  const p = state.players[playerId];
  if (!p) return null;

  const active = state.activePlayer === playerId;
  const low = p.integrity <= 5;

  return (
    <div
      className={[styles.bar, active ? styles.barActive : ''].filter(Boolean).join(' ')}
      data-tutorial={tutorialId}
    >
      <button
        type="button"
        className={[
          styles.avatar,
          targetable ? styles.avatarTargeted : '',
          underAttack ? styles.avatarAttacked : '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-player-avatar={playerId}
        onClick={onAvatarClick}
        disabled={!onAvatarClick}
        aria-label={targetable ? `Target ${p.name}` : p.name}
      >
        {p.name.slice(0, 1).toUpperCase()}
      </button>

      <span className={styles.barName}>{p.name}</span>

      {/*
        The arrow layer aims attack arrows here rather than at the avatar.
        Integrity is what an attack actually threatens, and the badge sits in
        the middle of the bar, so arrows stop short of the board edge instead of
        raking across the hand on their way to a corner.
      */}
      <span
        className={[
          styles.integrity,
          low ? styles.integrityLow : '',
          underAttack ? styles.integrityAttacked : '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-integrity-anchor={playerId}
      >
        <span className={styles.integrityValue}>{p.integrity}</span>
        <span className={styles.integrityLabel}>Integrity</span>
      </span>

      <span className={styles.counters}>
        <span className={styles.counter} title={`${p.hand.length} cards in hand`}>
          <UiIcon name="hand" size={13} />
          {p.hand.length}
        </span>
        <span className={styles.counter} title={`${p.deck.length} cards in deck`}>
          <UiIcon name="deck" size={13} />
          {p.deck.length}
        </span>
        <button
          type="button"
          className={`${styles.counter} ${styles.counterButton}`}
          title={`${p.dust.length} cards in Dust. Click to look through it.`}
          onClick={() => setDustOpen(true)}
        >
          <UiIcon name="dust" size={13} />
          {p.dust.length}
        </button>
        {trailing}
      </span>

      <Modal
        open={dustOpen}
        onClose={() => setDustOpen(false)}
        title={`${p.name} Dust`}
        subtitle="Dust is a public zone. Both players may read it at any time."
        size="large"
      >
        {p.dust.length === 0 ? (
          <p className={styles.emptyNote}>Nothing here yet.</p>
        ) : (
          <div className={styles.dustGrid}>
            {p.dust.map((id) => {
              const view = viewFromObject(state, id);
              return view ? <CardFrame key={id} card={view} size="hand" flat /> : null;
            })}
          </div>
        )}
      </Modal>
    </div>
  );
}

/**
 * Gas pips per chain: filled pips are Gas in the pool right now, hollow pips
 * are Gas this player could still make by exhausting a ready Wallet. Chains the
 * player has no Wallets for are left out, so the row stays readable.
 */
export function GasRow({ state, playerId }: { state: GameState; playerId: string }) {
  const p = state.players[playerId];
  if (!p) return null;

  const potential = walletProductionByChain(state, playerId);

  const rows: { chain: Chain | 'generic'; have: number; total: number }[] = [];
  if (p.gas.generic > 0 || potential.generic > 0) {
    rows.push({ chain: 'generic', have: p.gas.generic, total: p.gas.generic + potential.generic });
  }
  for (const chain of CHAINS) {
    const have = p.gas[chain];
    const total = have + potential[chain];
    if (total > 0) rows.push({ chain, have, total });
  }

  if (rows.length === 0) {
    return <span className={styles.gasCount}>No Gas available</span>;
  }

  return (
    <span className={styles.gasRow} aria-label="Gas available">
      {rows.map(({ chain, have, total }) => (
        <span key={chain} className={styles.gasChain} title={`${have} of ${total} ${chain.toUpperCase()} Gas`}>
          {chain === 'generic' ? (
            <FactionGlyph faction="neutral" size={12} color="var(--gas-generic)" />
          ) : (
            <FactionGlyph faction={chain} size={12} color={`var(--${chain})`} />
          )}
          <span className={styles.gasPips}>
            {Array.from({ length: Math.min(total, 12) }, (_, i) => (
              <span
                key={i}
                className={[styles.gasPip, i < have ? styles.gasPipFull : ''].filter(Boolean).join(' ')}
                style={{
                  ['--chain-color' as string]: chain === 'generic' ? 'var(--gas-generic)' : `var(--${chain})`,
                }}
              />
            ))}
          </span>
          <span className={styles.gasCount}>
            {have}/{total}
          </span>
        </span>
      ))}
    </span>
  );
}

/**
 * How much Gas each chain could still produce from ready, unfrozen Wallets.
 * Read from the Wallet's own `wallet` block, never from a card name.
 */
function walletProductionByChain(state: GameState, playerId: string): Record<Chain | 'generic', number> {
  const out: Record<Chain | 'generic', number> = {
    generic: 0,
    btc: 0,
    eth: 0,
    sol: 0,
    bnb: 0,
    xrp: 0,
  };
  const p = state.players[playerId];
  if (!p) return out;
  for (const id of p.battlefield.wallets) {
    const o = state.objects[id];
    if (!o || o.exhausted) continue;
    const wallet = profile(state, id).wallet;
    if (!wallet) continue;
    const bucket: Chain | 'generic' = wallet.chain === 'any' ? 'generic' : wallet.chain;
    out[bucket] += wallet.produces ?? 1;
  }
  return out;
}
